import { useState } from "react";
import { useNavigate } from "react-router";
import type { Channel, CommunityStructure, VoiceParticipant } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useUI } from "../../lib/uiStore";
import { Button, IconButton } from "../../components/ui/Button";
import { toast } from "../../components/ui/Toast";
import { IconBack, IconChatLine, IconInfo, IconMic, IconMicOff, IconSpeaker, IconUserMinus } from "../../components/icons";
import { useMe } from "../auth/AuthProvider";
import { usePrefs } from "../../lib/prefs";
import { useVoice, useVoiceStates } from "../voice/VoiceProvider";
import { RoomGrid, type ParticipantAction } from "../voice/RoomGrid";
import { VoiceControls, useShareBlockedReason } from "../voice/VoiceControls";
import { ChannelView } from "./ChannelView";
import { PrivateMark } from "./ChannelSidebar";
import { permsOf } from "./hooks";

/** Moderation actions for someone in a voice channel or on a stage. */
export function useVoiceModActions(structure: CommunityStructure, channel: Channel) {
  const me = useMe();
  const perms = permsOf(channel.permissions, structure.community.isOwner);
  return (p: VoiceParticipant): ParticipantAction[] => {
    if (p.user.id === me.id || p.user.id === structure.community.ownerId) return [];
    const mod = async (kind: "mute" | "unmute" | "disconnect") => {
      try {
        if (kind === "disconnect") await api.del(`/api/communities/${structure.community.id}/voice/${p.user.id}`);
        else await api.put(`/api/communities/${structure.community.id}/voice/${p.user.id}/mute`, { value: kind === "mute" });
      } catch (e) {
        toast.error(errorMessage(e));
      }
    };
    const out: ParticipantAction[] = [];
    // On stage the audience is listen-only anyway; muting is about speakers.
    if (perms.muteMembers && p.speaker !== false) {
      const modMuted = p.serverMuted;
      out.push({ label: modMuted ? "Unmute for everyone" : "Mute for everyone", icon: modMuted ? IconMic : IconMicOff, run: () => void mod(modMuted ? "unmute" : "mute") });
    }
    if (perms.disconnectMembers) out.push({ label: "Disconnect", icon: IconUserMinus, danger: true, run: () => void mod("disconnect") });
    return out;
  };
}

/** Voice channel: who's here, speaking indicators, screen shares, controls, and the channel's text chat. */
export function VoiceRoomView({ structure, channel, mobile }: { structure: CommunityStructure; channel: Channel; mobile?: boolean }) {
  const voice = useVoice();
  const navigate = useNavigate();
  const prefs = usePrefs();
  const [mobileChat, setMobileChat] = useState(false);
  const participants = useVoiceStates(structure.community.id, structure.voiceStates).filter((p) => p.channelId === channel.id);
  const here = voice.channelId === channel.id;
  const perms = permsOf(channel.permissions, structure.community.isOwner);
  // Desktop remembers the chat panel; on phones chat replaces the room, so it always starts closed.
  const desktopChat = useUI((s) => s.voiceChatOpen);
  const chatOpen = mobile ? mobileChat : desktopChat;
  const setChatOpen = (open: boolean) => (mobile ? setMobileChat(open) : useUI.getState().setVoiceChatOpen(open));
  const full = channel.userLimit > 0 && participants.length >= channel.userLimit && !here && !perms.disconnectMembers;
  const actions = useVoiceModActions(structure, channel);
  const shareReason = useShareBlockedReason(channel, structure);
  const streaming = participants.some((p) => p.streaming);

  const room = (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-bg">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        {mobile && <IconButton label="Back" icon={IconBack} tooltip={false} onClick={() => navigate(`/c/${structure.community.id}`)} />}
        <IconSpeaker className="icon size-5 shrink-0 text-fg-3" aria-hidden />
        <h1 className="truncate text-base font-semibold text-fg">{channel.name}</h1>
        {channel.isPrivate && <PrivateMark className="text-fg-3" />}
        <span className="ml-2 text-sm text-fg-3 tabular">
          {participants.length}
          {channel.userLimit ? `/${channel.userLimit}` : ""} connected
        </span>
        <div className="ml-auto">
          <IconButton label={chatOpen ? "Hide chat" : "Show chat"} icon={IconChatLine} active={chatOpen} onClick={() => setChatOpen(!chatOpen)} />
        </div>
      </header>
      <div className={cn("flex min-h-0 flex-1 flex-col items-center gap-6 overflow-y-auto p-4 md:p-6", streaming ? "justify-start" : "justify-center")}>
        {participants.length === 0 ? (
          <div className="text-center">
            <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-2xl bg-surface-2">
              <IconSpeaker className="icon size-7 text-fg-2" />
            </div>
            <h2 className="text-lg font-semibold">No one's here yet</h2>
            <p className="mt-1 text-sm text-fg-3">Join to start talking. Others will hear you when they connect.</p>
          </div>
        ) : (
          <div className={cn("flex w-full flex-col", streaming && "flex-1")}>
            <RoomGrid participants={participants} actions={actions} />
          </div>
        )}
        {!here && (
          <>
            <Button
              variant="primary"
              size="lg"
              icon={IconSpeaker}
              disabled={!perms.voice || full || voice.status === "connecting"}
              loading={voice.status === "connecting" && voice.channelId === channel.id}
              onClick={() => void voice.join(channel.id, structure.community.id, channel.name)}
            >
              {full ? "Channel full" : "Join voice"}
            </Button>
            <p className="flex items-center gap-1.5 text-sm text-fg-3">
              <IconInfo className="icon size-4 shrink-0" aria-hidden />
              Peer-to-peer audio for up to 12 people. Your microphone is only used while connected.
            </p>
          </>
        )}
      </div>
      {here && (
        <footer className="flex shrink-0 flex-col items-center gap-2 px-4 pb-5 pt-3">
          <VoiceControls shareReason={shareReason} />
          {prefs.pushToTalk && (
            <p className={cn("text-sm", voice.talking ? "text-success" : "text-fg-3")}>
              {voice.talking ? "Transmitting" : `Hold ${prefs.pushToTalkKey} to talk`}
            </p>
          )}
        </footer>
      )}
    </div>
  );

  if (!chatOpen) return room;
  if (mobile)
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <ChannelView structure={structure} channel={channel} mobile embedded onClose={() => setChatOpen(false)} />
      </div>
    );
  return (
    <div className="flex h-full min-w-0 flex-1">
      {room}
      <div className="flex w-[380px] shrink-0 border-l border-line xl:w-[420px]">
        <ChannelView structure={structure} channel={channel} embedded onClose={() => setChatOpen(false)} />
      </div>
    </div>
  );
}
